import { Dtos, FileId, SuretyAccountId, SuretyIndemnityAgreementId } from "@inrev/common";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { logError } from "../../../utils/error";
import { useRequest } from "../../../utils/request";
import {
	accountIndemnityAgreementsQueryKey,
	accountLatestIndemnityAgreementQueryKey,
	accountQueryKey,
} from "../account.keys";

export const useUploadCompletedIndemnityAgreement = (
	accountId: SuretyAccountId,
	indemnityAgreementId: SuretyIndemnityAgreementId,
) => {
	const queryClient = useQueryClient();
	const { post } = useRequest();
	const { triggerErrorModal } = useGlobalErrorMessageModal();

	const mutation = useMutation({
		mutationKey: ["accounts", accountId, "indemnity-agreements", indemnityAgreementId, "file"],
		mutationFn: async ({
			fileId,
			indemnityAgreementId,
		}: {
			fileId: FileId;
			indemnityAgreementId: SuretyIndemnityAgreementId;
		}) => {
			return await post<Dtos.IndemnityAgreement.Upload.Request>(
				`/v2/surety/accounts/${accountId}/indemnity-agreements/${indemnityAgreementId}/file`,
				{ fileId },
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: accountQueryKey(accountId) });
			queryClient.invalidateQueries({
				queryKey: accountIndemnityAgreementsQueryKey(accountId, indemnityAgreementId),
			});
			queryClient.invalidateQueries({
				queryKey: accountLatestIndemnityAgreementQueryKey(accountId),
			});
		},
		onError: (error: Error) => {
			logError(error);
			triggerErrorModal(error);
		},
	});

	return {
		uploadCompletedAgreement: mutation.mutate,
		uploadCompletedAgreementIsLoading: mutation.isPending,
		uploadCompletedAgreementSuccess: mutation.isSuccess,
	};
};
